import { useQuery } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

type DebouncedFilter<T> = {
  key: keyof T;
  debounceTime: number;
};

interface FilterWithPage extends Record<string, any> {
  page: number;
}

export function useDebouncedURLSearchParams<T extends FilterWithPage>(
  initialFilters: T,
  title: string,
  fetchFunction: (filters: T) => Promise<any>,
  debouncedFilters: DebouncedFilter<T>[] = []
) {
  const [searchParams, setSearchParams] = useSearchParams();

  // State for immediate UI updates
  const [uiFilters, setUiFilters] = useState(() => {
    const updatedFilters = { ...initialFilters };
    Object.keys(initialFilters).forEach((key) => {
      const paramValue = searchParams.get(key);
      if (paramValue !== null) {
        if (key === 'page') {
          (updatedFilters as any)[key] = Number(paramValue);
        } else (updatedFilters as any)[key] = paramValue;
      }
    });
    return updatedFilters;
  });

  const [debouncedFiltersState, setDebouncedFiltersState] = useState(uiFilters);

  const debouncedSetters = useMemo(() => {
    return debouncedFilters.reduce((acc, { key, debounceTime }) => {
      acc[key] = debounce((value: any) => {
        setDebouncedFiltersState((prev) => ({
          ...prev,
          [key]: value,
        }));
      }, debounceTime);
      return acc;
    }, {} as Record<keyof T, (value: any) => void>);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    Object.entries(uiFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) params.set(key, String(value));
    });
    // Replace, don't push: this effect re-runs whenever the URL changes, so a
    // push here re-adds an entry on every Back press and traps the user.
    setSearchParams(params, { replace: true });
  }, [uiFilters, setSearchParams]);

  useEffect(() => {
    Object.entries(uiFilters).forEach(([key, value]) => {
      if (debouncedSetters[key as keyof T]) {
        debouncedSetters[key as keyof T](value);
      } else {
        setDebouncedFiltersState((prev) => ({
          ...prev,
          [key]: value,
        }));
      }
    });
  }, [uiFilters, debouncedSetters]);

  const { data, isLoading } = useQuery({
    queryKey: [debouncedFiltersState, title],
    queryFn: () => fetchFunction(debouncedFiltersState),
  });

  return [uiFilters, setUiFilters, data, isLoading] as const;
}
